import {
  taxInvoiceService,
  type TaxInvoiceSearchRequest,
  type TaxInvoiceUpsertRequest,
} from '@/entities/tax-invoice/api/tax-invoice.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const taxInvoiceKeys = {
  all: ['tax-invoices'] as const,
  lists: () => [...taxInvoiceKeys.all, 'list'] as const,
  list: (search: Record<string, unknown>, pageable: Pageable) =>
    [...taxInvoiceKeys.lists(), search, pageable] as const,
  detail: (id: number) => [...taxInvoiceKeys.all, 'detail', id] as const,
};

export const useTaxInvoiceList = (
  search: TaxInvoiceSearchRequest,
  pageable: Pageable,
) =>
  usePaginatedQuery({
    queryKeyPrefix: taxInvoiceKeys.lists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      taxInvoiceService.getTaxInvoices(s, p),
  });

export const useTaxInvoiceDetail = (id: number) =>
  useQuery({
    queryKey: taxInvoiceKeys.detail(id),
    queryFn: () => taxInvoiceService.getTaxInvoiceDetail(id),
    enabled: id > 0,
  });

export const useCreateTaxInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TaxInvoiceUpsertRequest) =>
      taxInvoiceService.createTaxInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxInvoiceKeys.lists() });
    },
  });
};

export const useUpdateTaxInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: number;
      data: TaxInvoiceUpsertRequest;
    }) => taxInvoiceService.updateTaxInvoice(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: taxInvoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: taxInvoiceKeys.lists() });
    },
  });
};

export const useDeleteTaxInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => taxInvoiceService.deleteTaxInvoice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxInvoiceKeys.lists() });
    },
  });
};

export const useBulkDeleteTaxInvoices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => taxInvoiceService.bulkDeleteTaxInvoices(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxInvoiceKeys.lists() });
    },
  });
};

export const useIssueTaxInvoices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => taxInvoiceService.issueTaxInvoices(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxInvoiceKeys.lists() });
    },
  });
};

export const useCancelTaxInvoices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) => taxInvoiceService.cancelTaxInvoices(ids),
    // 건별로 일부만 처리됐을 수 있어 실패해도 목록을 다시 불러온다
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: taxInvoiceKeys.lists() });
    },
  });
};

export const useUploadTaxInvoices = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: TaxInvoiceUpsertRequest[]) =>
      taxInvoiceService.uploadTaxInvoices(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxInvoiceKeys.lists() });
    },
  });
};

export const useResetPublishedSettlement = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      settlementYearMonth,
      writeDate,
    }: {
      settlementYearMonth: string;
      writeDate: string;
    }) =>
      taxInvoiceService.resetPublishedSettlement(
        settlementYearMonth,
        writeDate,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taxInvoiceKeys.lists() });
    },
  });
};

/**
 * 역발행 목록 엑셀.
 * 서버가 파일을 만들 때까지 기다리므로 버튼 로딩을 이 mutation에 묶는다.
 */
export const useExportTaxInvoices = () =>
  useMutation({
    mutationFn: (params: TaxInvoiceSearchRequest) =>
      taxInvoiceService.exportTaxInvoices(params),
  });

export const useDownloadBulkTransfer = () =>
  useMutation({
    mutationFn: (yearMonth: string) =>
      taxInvoiceService.getBulkTransferRows(yearMonth),
  });
